import { Component, OnInit } from '@angular/core';
import { DataserviceService } from '../dataservice.service';
import { firstValueFrom } from 'rxjs';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NzNotificationService } from 'ng-zorro-antd/notification';

@Component({
  selector: 'app-onboarding-management',
  templateUrl: './onboarding-management.component.html',
  styleUrl: './onboarding-management.component.css'
})
export class OnboardingManagementComponent implements OnInit {
  currentUser: any;
  onboardingCards: any[] = [];
  loading: boolean = false;
  editModalVisible: boolean = false;
  editModalTitle: string = '';
  editModalContent: string = '';
  editModalFooter: string = '';
  createModalVisible: boolean = false;
  createModalTitle: string = '';
  createModalContent: string = '';
  createModalFooter: string = '';

  statusOptions: string[] = ['active', 'inactive'];

  selectedCard: any = null;
  selectedAction: any = null;
  actionForm: FormGroup;
  createForm: FormGroup;
  editForm: FormGroup;
  constructor(private data: DataserviceService, private fb: FormBuilder, private nzNotify: NzNotificationService) {
    this.currentUser = JSON.parse(localStorage.getItem('user')!);

    this.createForm = this.fb.group({
      name: ['', Validators.required],
      description: ['', Validators.required],
      status: ['', Validators.required],
      placeAfter: ['end'],
    });

    this.actionForm = this.fb.group({
      name: ['', Validators.required],
      description: ['', Validators.required],
    });

    this.editForm = this.fb.group({
      name: ['', Validators.required],
      description: ['', Validators.required],
      status: ['', Validators.required],
      placeAfter: [null],
    });
  }

  ngOnInit() {
    this.getOnboardingCards();
  }

  async getOnboardingCards() {
    const companyId = this.currentUser?.company_id;
    if (!companyId) {
      this.onboardingCards = [];
      return;
    }
    const onboarding = await firstValueFrom(this.data.getData(`onboarding/company/${companyId}`));
    const data = onboarding.data ?? [];
    this.onboardingCards = data.slice().sort((a: any, b: any) => {
      const pa = parseInt(a.priority, 10) || 0;
      const pb = parseInt(b.priority, 10) || 0;
      return pa - pb;
    });
  }

  async createCard(body: any) {
    const obj = {
      name: body.name,
      description: body.description,
      status: body.status,
      company_id: this.currentUser.company_id,
      created_by: this.currentUser.id,
      insert_after_id: body.placeAfter ?? 'end',
    };
    const onboarding = await firstValueFrom(this.data.postData(obj, 'onboarding/create'));
    if (onboarding.result == 'success') {
      this.nzNotify.success('Card created successfully', 'Card created successfully');
      this.createModalVisible = false;
      this.createForm.reset();
    } else {
      this.nzNotify.error('Failed to create card', 'Failed to create card');
    }
    this.getOnboardingCards();
  }

  async openCreateModal() {
    this.createModalVisible = true;
    this.createModalTitle = 'Create Card';
    this.createModalContent = 'Create Card';
    this.createModalFooter = 'Create Card';
    this.selectedCard = null;
    this.createForm.patchValue({
      name: '',
      description: '',
      status: 'active',
      placeAfter: 'end',
    });
  }

  getNextPosition(): number {
    if (!this.onboardingCards?.length) return 1;
    const nums = this.onboardingCards
      .map((c: any) => (c.priority != null ? parseInt(c.priority, 10) : 0))
      .filter((n: number) => !isNaN(n));
    return nums.length ? Math.max(...nums) + 1 : 1;
  }

  /** Options for "Place after" in create modal: start, after each bucket, end */
  get positionOptions(): { value: string; label: string }[] {
    const options: { value: string; label: string }[] = [
      { value: 'start', label: 'At beginning' },
    ];
    (this.onboardingCards || []).forEach((card: any) => {
      options.push({ value: card.id, label: `After ${card.name || 'Unnamed'}` });
    });
    options.push({ value: 'end', label: 'At end' });
    return options;
  }

  /** Same for edit, but exclude the card being edited so we don't show "After self" */
  get positionOptionsForEdit(): { value: string; label: string }[] {
    const options: { value: string; label: string }[] = [
      { value: 'start', label: 'At beginning' },
    ];
    (this.onboardingCards || []).forEach((card: any) => {
      if (card.id !== this.selectedCard?.id) {
        options.push({ value: card.id, label: `After ${card.name || 'Unnamed'}` });
      }
    });
    options.push({ value: 'end', label: 'At end' });
    return options;
  }

  async deleteCard(id: string) {
    const onboarding = await firstValueFrom(
      this.data.postDataNoBodyDotBody(
        { deleted_by: this.currentUser?.id },
        `onboarding/delete/${id}`
      )
    );
    if (onboarding.result == 'success') {
      this.nzNotify.success('Card deleted successfully', 'Card deleted successfully');
      this.getOnboardingCards();
    } else {
      this.nzNotify.error('Failed to delete card', 'Failed to delete card');
    }
    this.getOnboardingCards();
  }

  openEditModal(id: string) {
    this.editModalVisible = true;
    this.editModalTitle = 'Edit Card';
    this.editModalContent = 'Edit Card';
    this.editModalFooter = 'Edit Card';
    this.selectedCard = this.onboardingCards.find((card: any) => card.id === id);
    this.editForm.patchValue({
      name: this.selectedCard.name,
      description: this.selectedCard.description,
      status: this.selectedCard.status,
      placeAfter: this.getPlaceAfterForCard(this.selectedCard),
    });
  }

  /** Id of the bucket that is right before this card (or 'start' if first) */
  getPlaceAfterForCard(card: any): string {
    const idx = this.onboardingCards.findIndex((c: any) => c.id === card.id);
    if (idx <= 0) return 'start';
    return this.onboardingCards[idx - 1].id;
  }

  async editCard(id: string, body: any) {
    const payload = {
      name: body.name,
      description: body.description,
      status: body.status,
      updated_by: this.currentUser.id,
      company_id: this.selectedCard.company_id,
      move_to_after_id: body.placeAfter ?? 'end',
    };
    const onboarding = await firstValueFrom(this.data.postData(payload, `onboarding/update/${id}`));
    if (onboarding.result == 'success') {
      this.nzNotify.success('Card updated successfully', 'Card updated successfully');
      this.editModalVisible = false;
      this.editForm.reset();
    } else {
      this.nzNotify.error('Failed to update card', 'Failed to update card');
    }
    this.getOnboardingCards();
  }

  async addAction(id: string, body: any) {
    body.created_by = this.currentUser.id;
    body.company_id = this.selectedCard.company_id;
    const onboarding = await firstValueFrom(this.data.postData(body, `onboarding/create-action/${id}`));
    if (onboarding.result == 'success') {
      this.nzNotify.success('Action created successfully', 'Action created successfully');
      this.actionForm.reset();
      this.getOnboardingCards();
    } else {
      this.nzNotify.error('Failed to create action', 'Failed to create action');
    }
    this.getOnboardingCards();
  }

  async deleteAction(id: string, actionId: string) {
    const onboarding = await firstValueFrom(this.data.deleteData(`onboarding/delete-action/${id}/${actionId}`));
    if (onboarding.result == 'success') {
      this.nzNotify.success('Action deleted successfully', 'Action deleted successfully');
      this.actionForm.reset();
      this.getOnboardingCards();
    } else {
      this.nzNotify.error('Failed to delete action', 'Failed to delete action');
    }
    this.getOnboardingCards();
  }

  async editAction(id: string, actionId: string, body: any) {
    const onboarding = await firstValueFrom(this.data.postData(`onboarding/update-action/${id}/${actionId}`, body));
    if (onboarding.result == 'success') {
      this.nzNotify.success('Action updated successfully', 'Action updated successfully');
      this.actionForm.reset();
      this.getOnboardingCards();
    } else {
      this.nzNotify.error('Failed to update action', 'Failed to update action');
    }
    this.getOnboardingCards();
  }

}
